/**
 * BulkAssignUserModal — Modal fuer Bearbeiter-Zuweisung via Bulk-Aktion (ADMIN-06).
 *
 * User-Dropdown aus store.users (wird beim Init geladen, API: leadsApi.listUsers).
 * Erstes Item: "Niemand (null)" — userId=null setzt assignedUserId auf null (zuruecksetzen).
 *
 * Submit → store.runBulkAction({ action: 'assign_user', payload: { userId } })
 * Erfolg: Banner + auto-close nach 1.5s.
 *
 * CI-Farben: #E9FF57 + #1A1A1A. NIE weisse Schrift auf Gelb.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useLeadBoard } from '../store/useLeadBoard';
import type { BulkActionResponse } from '../services/leadsApi';

interface Props {
  onClose: () => void;
}

export default function BulkAssignUserModal({ onClose }: Props) {
  const selectedLeadIds = useLeadBoard((s) => s.selectedLeadIds);
  const users = useLeadBoard((s) => s.users);
  const runBulkAction = useLeadBoard((s) => s.runBulkAction);

  // '' = kein Wert gewaehlt; 'none' = Niemand (null); '123' = userId
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkActionResponse | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (selectedValue === '') {
      setInputError('Bitte Bearbeiter auswaehlen');
      return;
    }
    setInputError(null);
    const userId = selectedValue === 'none' ? null : Number(selectedValue);
    setBusy(true);
    try {
      const res = await runBulkAction({
        leadIds: Array.from(selectedLeadIds),
        action: 'assign_user',
        payload: { userId },
      });
      setResult(res);
      if (res.failed.length === 0) {
        setTimeout(() => onClose(), 1500);
      }
    } catch (e: any) {
      setInputError(e?.message ?? 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg max-w-sm w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <h2 className="text-base font-semibold text-[#1A1A1A]">Bearbeiter zuweisen</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-[#1A1A1A] rounded p-1"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Body */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Bearbeiter fuer{' '}
            <span className="font-semibold text-[#1A1A1A]">{selectedLeadIds.size}</span>{' '}
            {selectedLeadIds.size === 1 ? 'Karte' : 'Karten'} setzen:
          </p>

          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
              Bearbeiter
            </label>
            <select
              value={selectedValue}
              onChange={(e) => {
                setSelectedValue(e.target.value);
                setInputError(null);
              }}
              disabled={busy}
              className={
                'w-full border rounded px-3 py-2 text-sm text-[#1A1A1A] bg-white ' +
                (inputError ? 'border-red-500' : 'border-[#DAD9D9]')
              }
            >
              <option value="">– Auswaehlen –</option>
              <option value="none">Niemand (Zuweisung aufheben)</option>
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
            {inputError && (
              <p className="text-red-600 text-xs mt-1">{inputError}</p>
            )}
          </div>

          {/* Erfolgs-Banner */}
          {result && result.failed.length === 0 && (
            <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded text-sm font-medium">
              {result.successful} {result.successful === 1 ? 'Karte' : 'Karten'} aktualisiert.
            </div>
          )}

          {/* Fehler-Banner */}
          {result && result.failed.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {result.successful > 0 && <p>{result.successful} erfolgreich.</p>}
              <p>{result.failed.length} fehlgeschlagen.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || selectedValue === ''}
            className="px-4 py-2 text-sm rounded bg-[#E9FF57] text-[#1A1A1A] font-medium hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Wird ausgefuehrt...' : 'Zuweisen'}
          </button>
        </footer>
      </div>
    </div>
  );
}
